<template>
  <AppModal :model-value="modelValue" :title="title" :subtitle="config.formSubtitle || ''" :size="config.formSize || 'md'" :persistent="submitState.submitting" @close="$emit('update:modelValue', false)">
    <form id="resource-form" novalidate class="grid gap-4 sm:grid-cols-2" @submit.prevent="onSubmit">
      <AlertBanner v-if="banner" tone="error" class="sm:col-span-2">
        <p>{{ banner }}</p>
        <ul v-if="orphanErrors.length" class="mt-1 list-inside list-disc text-xs">
          <li v-for="(message, i) in orphanErrors" :key="i">{{ message }}</li>
        </ul>
      </AlertBanner>

      <FormField
        v-for="field in visibleFields"
        :key="field.name"
        v-model="form[field.name]"
        :field="field"
        :form="form"
        :errors="errorsFor(field)"
        :disabled="isDisabled(field)"
      />
    </form>

    <template #footer>
      <AppButton variant="outline" :disabled="submitState.submitting" @click="$emit('update:modelValue', false)">Annuler</AppButton>
      <AppButton type="submit" form="resource-form" :loading="submitState.submitting">{{ editing ? 'Enregistrer' : config.submitLabel || 'Créer' }}</AppButton>
    </template>
  </AppModal>
</template>

<script>
import AppModal from '@/components/ui/AppModal.vue'
import AppButton from '@/components/ui/AppButton.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import FormField from '@/components/ui/FormField.vue'
import { useSubmit } from '@/composables/useSubmit'
import { toast } from '@/stores/toast'
import { normalizeAmountInput, validateAmount } from '@/utils/money'
import { localInputToIso } from '@/utils/dates'
import { newLabel } from '@/utils/labels'
import { clearLookups } from '@/api/lookups'

const EMPTY = { toggle: false }

function initialValue(field, record) {
  if (record && record[field.name] !== undefined) {
    const v = record[field.name]
    if (v === null) return field.type === 'toggle' ? false : ''
    return field.type === 'date' ? String(v).slice(0, 10) : v
  }
  if (field.default !== undefined) return typeof field.default === 'function' ? field.default() : field.default
  return field.type in EMPTY ? EMPTY[field.type] : ''
}

/**
 * Formulaire de création/édition piloté par une configuration :
 *   config.fields    → champs (voir FormField)
 *   config.toForm    → (record) => valeurs initiales (facultatif)
 *   config.toPayload → (form, { editing }) => corps de requête (facultatif)
 *   config.mapErrors → (errors, form) => errors, pour réattribuer des erreurs serveur avant affichage
 *   field.errorKey   → clé d'erreur du serveur si elle diffère du nom du champ (ex. « account.email »)
 *   config.afterSave → (saved, form) => Promise, après création/modification
 */
export default {
  name: 'ResourceForm',
  components: { AppModal, AppButton, AlertBanner, FormField },
  props: {
    modelValue: { type: Boolean, default: false },
    config: { type: Object, required: true },
    record: { type: Object, default: null }
  },
  emits: ['update:modelValue', 'saved'],
  setup() {
    const { state: submitState, submit } = useSubmit()
    return { submitState, submit }
  },
  data() {
    return { form: {}, clientErrors: {} }
  },
  computed: {
    editing() {
      return !!this.record
    },
    title() {
      const c = this.config
      return this.editing ? c.editTitle || `Modifier ${c.singular}` : c.createTitle || newLabel(c)
    },
    visibleFields() {
      return this.config.fields.filter((f) => this.isVisible(f))
    },
    banner() {
      return this.submitState.message
    },
    // Erreurs du serveur, éventuellement réattribuées (ex. email → account.email).
    serverErrors() {
      const raw = this.submitState.fieldErrors
      return this.config.mapErrors ? this.config.mapErrors(raw, this.form) : raw
    },
    // Erreurs qu'aucun champ affiché ne porte : elles vont dans le bandeau général (filet de repli).
    orphanErrors() {
      const known = new Set(this.visibleFields.map((f) => f.errorKey || f.name))
      return Object.entries(this.serverErrors)
        .filter(([name]) => !known.has(name))
        .flatMap(([, messages]) => messages)
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.reset()
      }
    }
  },
  methods: {
    reset() {
      const base = {}
      for (const field of this.config.fields) base[field.name] = initialValue(field, this.record)
      this.form = this.record && this.config.toForm ? { ...base, ...this.config.toForm(this.record) } : base
      this.clientErrors = {}
      this.submitState.message = ''
      this.submitState.fieldErrors = {}
    },
    isVisible(field) {
      if (field.createOnly && this.editing) return false
      if (field.editOnly && !this.editing) return false
      return field.visible ? field.visible(this.form, this.editing) : true
    },
    isDisabled(field) {
      return field.disabled ? field.disabled(this.form, this.editing) : false
    },
    errorsFor(field) {
      return this.clientErrors[field.name] || this.serverErrors[field.errorKey || field.name] || []
    },
    validate() {
      const errors = {}
      for (const field of this.visibleFields) {
        const value = this.form[field.name]
        const empty = value === '' || value === null || value === undefined
        if (field.required && !(field.requiredOnEdit === false && this.editing) && empty) {
          errors[field.name] = ['Ce champ est obligatoire.']
        } else if (field.type === 'money' && !empty) {
          const message = validateAmount(value, { allowZero: field.allowZero })
          if (message) errors[field.name] = [message]
        } else if (field.type === 'file' && value) {
          const message = field.validateFile && field.validateFile(value)
          if (message) errors[field.name] = [message]
        }
      }
      if (this.config.validate) Object.assign(errors, this.config.validate(this.form, this.editing))
      this.clientErrors = errors
      return Object.keys(errors).length === 0
    },
    defaultPayload() {
      const payload = {}
      for (const field of this.visibleFields) {
        if (field.virtual || field.type === 'file') continue
        const raw = this.form[field.name]
        const empty = raw === '' || raw === null || raw === undefined
        if (empty) {
          if (field.nullable) payload[field.name] = null
          continue
        }
        if (field.type === 'money') payload[field.name] = normalizeAmountInput(raw)
        else if (field.type === 'integer' || field.type === 'number') payload[field.name] = Number(raw)
        else if (field.type === 'lookup' && !field.stringValue) payload[field.name] = Number(raw)
        else if (field.type === 'datetime') payload[field.name] = localInputToIso(raw)
        else payload[field.name] = raw
      }
      return payload
    },
    /** « Recette enregistrée. » / « Véhicule modifié. » (accord via config.feminine). */
    doneMessage() {
      if (!this.editing && this.config.createdMessage) return this.config.createdMessage
      const label = this.config.singular.charAt(0).toUpperCase() + this.config.singular.slice(1)
      const e = this.config.feminine ? 'e' : ''
      return this.editing ? `${label} modifié${e}.` : `${label} enregistré${e}.`
    },
    async onSubmit() {
      if (!this.validate()) return
      const payload = this.config.toPayload ? this.config.toPayload(this.form, { editing: this.editing, defaults: this.defaultPayload() }) : this.defaultPayload()
      const saved = await this.submit(() => (this.editing ? this.config.api.update(this.record.id, payload) : this.config.api.create(payload)))
      if (saved === undefined) return
      clearLookups()
      let followUpError = null
      if (this.config.afterSave) {
        try {
          await this.config.afterSave(saved, this.form)
        } catch (e) {
          followUpError = e
        }
      }
      toast.success(this.doneMessage())
      if (followUpError) toast.error(followUpError.message)
      this.$emit('saved', saved)
      this.$emit('update:modelValue', false)
    }
  }
}
</script>
